import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ZakatTypeSelector from '@/components/zakat/ZakatTypeSelector'
import ZakatSummary from '@/components/zakat/ZakatSummary'
import ZakatAlertModal from '@/components/zakat/ZakatAlertModal'
import ZakatHowToModal from '@/components/zakat/ZakatHowToModal'
import ZakatMoneyForm from '@/components/zakat/forms/ZakatMoneyForm'
import ZakatGoldForm from '@/components/zakat/forms/ZakatGoldForm'
import ZakatSilverForm from '@/components/zakat/forms/ZakatSilverForm'
import ZakatCropsForm from '@/components/zakat/forms/ZakatCropsForm'
import ZakatLivestockForm from '@/components/zakat/forms/ZakatLivestockForm'
import ZakatFreelanceForm from '@/components/zakat/forms/ZakatFreelanceForm'
import { calculateZakat } from '@/services/zakatCalculationService'
import type {
  ZakatType,
  CalendarType,
  ZakatMoneyInput,
  ZakatGoldInput,
  ZakatSilverInput,
  ZakatCropsInput,
  ZakatLivestockInput,
  ZakatFreelanceInput,
  ZakatCalculationResult,
} from '@/types/zakat'

export default function ZakatCalculatorPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [selectedType, setSelectedType] = useState<ZakatType>('money')
  const [calendar, setCalendar] = useState<CalendarType>('hijri')
  const [calculationResult, setCalculationResult] = useState<ZakatCalculationResult | null>(null)
  const [showAlert, setShowAlert] = useState(false)
  const [showHowTo, setShowHowTo] = useState(false)

  // Input states for each type
  const [moneyInput, setMoneyInput] = useState<ZakatMoneyInput>({
    amount: 0,
    goldPricePerGram24: 0,
  })

  const [goldInput, setGoldInput] = useState<ZakatGoldInput>({
    grams18: 0,
    price18: 0,
    grams21: 0,
    price21: 0,
    grams24: 0,
    price24: 0,
  })

  const [silverInput, setSilverInput] = useState<ZakatSilverInput>({
    grams: 0,
    pricePerGram: 0,
  })

  const [cropsInput, setCropsInput] = useState<ZakatCropsInput>({
    irrigationType: 'withCost',
    kilograms: 0,
    pricePerKilogram: 0,
  })

  const [livestockInput, setLivestockInput] = useState<ZakatLivestockInput>({
    sheep: 0,
    camels: 0,
    cattle: 0,
  })

  const [freelanceInput, setFreelanceInput] = useState<ZakatFreelanceInput>({
    revenues: 0,
    wagesAndExpenses: 0,
    debts: 0,
    goldPricePerGram24: 0,
    livingExpenses: 0,
  })

  const handleCalculate = () => {
    let result: ZakatCalculationResult

    try {
      switch (selectedType) {
        case 'money':
          result = calculateZakat({
            type: 'money',
            data: moneyInput,
            calendar,
          })
          break
        case 'gold':
          result = calculateZakat({
            type: 'gold',
            data: goldInput,
            calendar,
          })
          break
        case 'silver':
          result = calculateZakat({
            type: 'silver',
            data: silverInput,
            calendar,
          })
          break
        case 'crops':
          result = calculateZakat({
            type: 'crops',
            data: cropsInput,
            calendar,
          })
          break
        case 'livestock':
          result = calculateZakat({
            type: 'livestock',
            data: livestockInput,
            calendar,
          })
          break
        case 'freelance':
          result = calculateZakat({
            type: 'freelance',
            data: freelanceInput,
            calendar,
          })
          break
        default:
          return
      }

      setCalculationResult(result)
      
      // Show alert if nisab is not reached
      if (result && result.nisab > 0 && !result.isZakatDue) {
        setShowAlert(true)
      }
    } catch (error) {
      console.error('Error calculating Zakat:', error)
    }
  }

  const handleShowHowTo = () => {
    setShowHowTo(true)
  }

  const renderForm = () => {
    switch (selectedType) {
      case 'money':
        return (
          <ZakatMoneyForm
            input={moneyInput}
            calendar={calendar}
            onInputChange={setMoneyInput}
            onCalendarChange={setCalendar}
            onCalculate={handleCalculate}
          />
        )
      case 'gold':
        return (
          <ZakatGoldForm
            input={goldInput}
            calendar={calendar}
            onInputChange={setGoldInput}
            onCalendarChange={setCalendar}
            onCalculate={handleCalculate}
          />
        )
      case 'silver':
        return (
          <ZakatSilverForm
            input={silverInput}
            calendar={calendar}
            onInputChange={setSilverInput}
            onCalendarChange={setCalendar}
            onCalculate={handleCalculate}
          />
        )
      case 'crops':
        return (
          <ZakatCropsForm
            input={cropsInput}
            calendar={calendar}
            onInputChange={setCropsInput}
            onCalendarChange={setCalendar}
            onCalculate={handleCalculate}
          />
        )
      case 'livestock':
        return (
          <ZakatLivestockForm
            input={livestockInput}
            calendar={calendar}
            onInputChange={setLivestockInput}
            onCalendarChange={setCalendar}
            onCalculate={handleCalculate}
          />
        )
      case 'freelance':
        return (
          <ZakatFreelanceForm
            input={freelanceInput}
            calendar={calendar}
            onInputChange={setFreelanceInput}
            onCalendarChange={setCalendar}
            onCalculate={handleCalculate}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <Helmet>
        <title>{t('zakat.title')} - سكينة</title>
        <link rel="icon" type="image/png" href="/assets/logo.png" />
        <meta
          name="description"
          content={t('public.zakat.seo.description')}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Zakat Type Selector */}
        <ZakatTypeSelector
          selectedType={selectedType}
          onTypeChange={setSelectedType}
        />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="lg:col-span-2">
            {renderForm()}
          </div>

          {/* Summary Section */}
          <div className="lg:col-span-1">
            <ZakatSummary result={calculationResult} onShowHowTo={handleShowHowTo} />
          </div>
        </div>

        {/* Alert Modal */}
        <ZakatAlertModal
          isOpen={showAlert}
          message="ليس عليك زكاة (لم تصل للنصاب)"
          onClose={() => setShowAlert(false)}
        />

        {/* How To Calculate Modal */}
        <ZakatHowToModal
          isOpen={showHowTo}
          zakatType={selectedType}
          result={calculationResult}
          onClose={() => setShowHowTo(false)}
        />
      </div>
    </>
  )
}
